import { src } from '@/utils/common';
import SWidget from './style.module.scss';
import { useWebPResourceCompatibleFn } from '../../../hooks/useWebPResource';
import FadeIn from '@/components/commonComponents/fadeIn';
import { useEffect, useRef, useState } from 'react';
import { WebPNativeImage, WebPNextImage } from '@/components/WebPImage';
import { Zoom } from 'react-awesome-reveal';
import { Element } from 'react-scroll';
import useKwaiPlayer from '@/hooks/useKwaiPlayer';

const BaseHeader = ({ title, subTitle, background }) => {
  return (
    <>
      <div className={SWidget['background']}>
        <WebPNextImage src={background} alt={title} layout="fill" />
      </div>
      <FadeIn direction={null}>
        <div className={SWidget['content']}>
          <div className={SWidget['title']}>{title}</div>
          <div className={SWidget['sub-title']}>{subTitle}</div>
        </div>
      </FadeIn>
    </>
  );
};

export const ActivityH1 = ({ title, subTitle }) => {
  return (
    <div className={SWidget['activity-h1']}>
      <BaseHeader
        title={title}
        subTitle={subTitle}
        background={'/img/activity/publish/h1-background.webp'}
      />
    </div>
  );
};

export const ActivityH2 = ({ title, subTitle }) => {
  return (
    <FadeIn>
      <div className={SWidget['activity-h2']}>
        <BaseHeader
          title={title}
          subTitle={subTitle}
          background={'/img/activity/publish/h2-background.webp'}
        />
      </div>
    </FadeIn>
  );
};

export const LVSH1 = ({ title, subTitle }) => {
  return (
    <div className={SWidget['lvs-h1']}>
      <BaseHeader
        title={title}
        subTitle={subTitle}
        background={'/img/activity/lvs/h1-background.webp'}
      />
    </div>
  );
};

export const ActivityCard = ({
  interval,
  title,
  slogan,
  desc,
  cast,
  background,
}: {
  interval: string;
  title: string;
  slogan?: string;
  desc?: string;
  cast?: { name: string; desc: string }[];
  background?: { url: string; hoverUrl: string };
}) => {
  const webPResourceHandleFn = useWebPResourceCompatibleFn();
  const [isHover, setIsHover] = useState<boolean>(false);
  return (
    <div
      className={SWidget['activity-card']}
      style={
        background && {
          backgroundImage: `url(${webPResourceHandleFn(
            src(
              isHover && background.hoverUrl
                ? `${background.hoverUrl}`
                : background.url
            )
          )})`,
        }
      }
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <div className={SWidget['interval']}>{interval}</div>
      <div className={SWidget['slogan']}>{slogan}</div>
      <div className={SWidget['title']}>{title}</div>
      {desc && <div className={SWidget['desc']}>{desc}</div>}
      <div className={SWidget['cast']}>
        {cast?.map((item, index) => (
          <div className={SWidget['item']} key={index}>
            <div className={SWidget['name']}>{item.name}</div>
            <div className={SWidget['divider']}>-</div>
            <div className={SWidget['item-desc']}>{item.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ActivityVideo = ({
  url,
  poster,
}: {
  url: string;
  poster?: string;
}) => {
  const webPResourceHandleFn = useWebPResourceCompatibleFn();
  const [showPlayBtn, setShowPlayBtn] = useState<boolean>(true);
  const videoRef = useRef<HTMLVideoElement>();
  const KwaiPlayerReact = useKwaiPlayer();

  useEffect(() => {
    setShowPlayBtn(true);
  }, [url]);

  return (
    <div className={SWidget['video']}>
      {KwaiPlayerReact && (
        <KwaiPlayerReact
          ref={videoRef}
          src={url}
          poster={webPResourceHandleFn(src(poster))}
          className={SWidget['ks-player']}
          onPlaying={() => setShowPlayBtn(false)}
          onPause={() => setShowPlayBtn(true)}
          onError={() => setShowPlayBtn(false)}
          controls
          playsInline
        />
      )}
      {showPlayBtn && (
        <div
          className={SWidget['play-btn']}
          onClick={() => videoRef.current?.play()}
        >
          <Zoom duration={100}>
            <WebPNativeImage
              src={src('/img/activity/publish/play-btn.webp')}
              alt={'play'}
              width="100%"
            />
          </Zoom>
        </div>
      )}
    </div>
  );
};

export type DecoType = 1 | 2 | 3;
export const ActivityDecorator = ({
  type,
  children,
}: {
  type: DecoType;
  children: JSX.Element;
}) => {
  const { elementName } = children.props;
  return (
    <Element name={elementName}>
      <div className={SWidget['deco-container']}>
        <WebPNativeImage
          src={src(`/img/activity/publish/deco${type}.webp`)}
          className={`${SWidget['deco']} ${SWidget[`type${type}`]}`}
        />
        <div className={SWidget['deco-content']}>{children}</div>
      </div>
    </Element>
  );
};
